import {
  Box,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useMapa } from "../../../contexts/mapaContext";
import DraggableModal from "../draggableModal";

// A causa vem pronta do backend (causaDesconexao em findmanyOnus): coluna
// causaDesconexaoOnu, "Queda de sessão" (rx ok + sessão caída) ou "Desconhecido".
const DESCONHECIDO = "Desconhecido";
const TODAS = "";

const ellipsis = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
};

const serialDaOnu = (id) => (id ?? "").replace(/_onu$/i, "");

const OnusDownPanel = ({ open, onClose }) => {
  const theme = useTheme();
  const { onusMap, onusInvalidMap, loadingDetails } = useMapa();
  const [query, setQuery] = useState("");
  const [causaFiltro, setCausaFiltro] = useState(TODAS);

  // down = status 0 (statusSessao 0 ou rx <= -40, calculado no backend)
  const down = useMemo(() => {
    const list = [];
    for (const map of [onusMap, onusInvalidMap]) {
      map?.forEach((onu) => {
        if (onu?.data?.status?.value !== 0) return;
        list.push({
          id: onu.id,
          titulo: onu.titulo || "--",
          serial: serialDaOnu(onu.id),
          cto: onu.cto,
          causa: onu.causaDesconexao || DESCONHECIDO,
        });
      });
    }
    return list;
  }, [onusMap, onusInvalidMap]);

  // total por causa sobre todas as ONUs down (opções do filtro)
  const totalPorCausa = useMemo(() => {
    const contagem = new Map();
    for (const o of down)
      contagem.set(o.causa, (contagem.get(o.causa) ?? 0) + 1);
    return [...contagem].sort((a, b) => b[1] - a[1]);
  }, [down]);

  // a causa escolhida pode sumir num resync (nenhuma ONU com ela)
  const causaAtiva = totalPorCausa.some(([c]) => c === causaFiltro)
    ? causaFiltro
    : TODAS;

  // busca e filtro aplicados, ordenado por cliente
  const lista = useMemo(() => {
    const q = query.trim().toLowerCase();
    return down
      .filter(
        (o) =>
          (!causaAtiva || o.causa === causaAtiva) &&
          (!q ||
            `${o.titulo} ${o.serial} ${o.cto ?? ""} ${o.causa}`
              .toLowerCase()
              .includes(q)),
      )
      .sort((a, b) => a.titulo.localeCompare(b.titulo));
  }, [down, query, causaAtiva]);

  const loading = loadingDetails && down.length === 0;

  return (
    <DraggableModal
      open={open}
      onClose={onClose}
      title={`ONUs down (${down.length})`}
      storageKey="easyMapsOnusDownPanel"
    >
      <Stack gap={1} sx={{ px: 1.5, pb: 1.25 }}>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar cliente, serial, CTO ou causa"
          inputProps={{ "aria-label": "Buscar cliente, serial, CTO ou causa" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
            sx: { fontSize: "0.82rem" },
          }}
        />
        <TextField
          select
          size="small"
          fullWidth
          label="Causa"
          value={causaAtiva}
          onChange={(e) => setCausaFiltro(e.target.value)}
          InputProps={{ sx: { fontSize: "0.82rem" } }}
          // o menu precisa ficar acima do modal (zIndex 1501)
          SelectProps={{ MenuProps: { sx: { zIndex: 1502 } } }}
        >
          <MenuItem value={TODAS} sx={{ fontSize: "0.82rem" }}>
            Todas ({down.length})
          </MenuItem>
          {totalPorCausa.map(([causa, total]) => (
            <MenuItem key={causa} value={causa} sx={{ fontSize: "0.82rem" }}>
              {causa} ({total})
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box
        component="ol"
        aria-label="ONUs down"
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          flex: 1,
          overflowY: "auto",
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {loading &&
          [0, 1, 2].map((i) => (
            <Stack
              key={i}
              component="li"
              aria-hidden="true"
              gap={0.5}
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Skeleton variant="text" width="45%" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </Stack>
          ))}

        {!loading &&
          lista.map((o) => (
            <Stack
              key={o.id}
              component="li"
              gap={0.25}
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack direction="row" alignItems="baseline" gap={2}>
                <Box
                  component="span"
                  title={o.titulo}
                  sx={{
                    flex: 1,
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    ...ellipsis,
                  }}
                >
                  {o.titulo}
                </Box>
                <Box
                  component="span"
                  title={o.serial}
                  sx={{
                    fontSize: "0.78rem",
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                    color: theme.palette.text.secondary,
                  }}
                >
                  {o.serial}
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    flexShrink: 0,
                    backgroundColor:
                      o.causa === DESCONHECIDO
                        ? theme.palette.text.disabled
                        : theme.palette.error.main,
                  }}
                />
                <Box
                  component="span"
                  title={`${o.causa}${o.cto ? ` · ${o.cto}` : ""}`}
                  sx={{
                    fontSize: "0.72rem",
                    color: theme.palette.text.secondary,
                    ...ellipsis,
                  }}
                >
                  {o.causa}
                  {o.cto ? ` · ${o.cto}` : ""}
                </Box>
              </Stack>
            </Stack>
          ))}

        {!loading && lista.length === 0 && (
          <Box
            component="li"
            sx={{
              py: 4,
              px: 2,
              textAlign: "center",
              fontSize: "0.82rem",
              color: theme.palette.text.secondary,
            }}
          >
            {down.length === 0
              ? "Nenhuma ONU down no momento."
              : `Nenhuma ONU encontrada${query ? ` para “${query}”` : ""}.`}
          </Box>
        )}
      </Box>
    </DraggableModal>
  );
};

export default OnusDownPanel;
